import React, {
    useEffect,
    useCallback,
    useReducer,
    useMemo,
    memo,
} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router';
import { withCookies } from 'react-cookie';

import Carousel from 'components/bronson/Carousel';
import CountDown from 'components/CountDown';
import Tag from 'components/bronson/Tag';
import VehicleFavouriteIcon from 'components/shared/VehicleFavouriteIcon';
import Header from 'components/auctions/AuctionVehicleCard/Header';
import Body from 'components/auctions/AuctionVehicleCard/Body';
import Footer from 'components/auctions/AuctionVehicleCard/Footer';
import ModalDetailVehicle from 'components/auctions/ModalDetailVehicle';

import Traductor from 'components/Traductor';
import VehicleService from 'services/VehicleService';
import {
    checkCountDownStatus,
    getCountDownStyles,
} from 'js/components/shared/vehicleFunctions';
import { isAppleDeviceOrNavegatorForDates, getImageSource } from 'Functions';
import Globals from '../../../Globals';
import useServerTime from 'hooks/useServerTime';
import * as vehicleActions from '../../../redux/actions/auctions';
import { vehicleProptypes } from './CardTypes';

import 'css/components/shared/vehicleCard/VehicleCard.css';

const initialVehicleCardState = {
    loading: true,
    disabledVehicleWebSalesButtons: true,
    showDetail: false,
    countDownStatus: null,
};

const vehicleCardReducer = (state, { type, payload }) => {
    switch (type) {
        case 'SET_MAX_BID_AWS':
            return { ...state, maxBidAWS: payload };
        case 'SET_CURRENT_BID':
            return { ...state, currentVehicleBid: payload };
        case 'SET_LOADING':
            return { ...state, loading: payload };
        case 'CHANGE_WEBSALES_STATUS':
            return { ...state, disabledVehicleWebSalesButtons: payload };
        case 'UPDATE_COUNTDOWN_STATUS':
            return {
                ...state,
                countDownStatus: payload,
            };
        case 'SHOW_DETAIL':
            return {
                ...state,
                showDetail: !state.showDetail,
            };
        default:
            return state;
    }
};

const VehicleAuctionCard = ({
    cookies,
    vehicle,
    updateVehiclesList,
    serverTime,
    fetchServerTime,
}) => {
    const t = useMemo(() => new Traductor(), []);
    const optionsCarousel = {
        id: 'c-vehicle-card__carousel-' + vehicle.Id,
        next: true,
        images: [getImageSource(vehicle?.ImageSource)],
        flickity: {
            imagesLoaded: false,
        },
    };
    const [state, dispatch] = useReducer(
        vehicleCardReducer,
        initialVehicleCardState
    );

    const getCountDownStatus = useCallback(() => {
        return checkCountDownStatus(
            isAppleDeviceOrNavegatorForDates(vehicle?.PublicationDate),
            isAppleDeviceOrNavegatorForDates(
                vehicle?.PublicationEndDateVehicle ||
                    vehicle?.PublicationEndDate
            ),
            serverTime
        );
    }, [
        serverTime,
        vehicle?.PublicationDate,
        vehicle?.PublicationEndDate,
        vehicle?.PublicationEndDateVehicle,
    ]);

    const updateCountDownStatus = useCallback(
        value => {
            return dispatch({
                type: 'UPDATE_COUNTDOWN_STATUS',
                payload:
                    (value && state.countDownStatus + 1) || //callback from countdown ended timer
                    getCountDownStatus(),
            });
        },
        [getCountDownStatus, state.countDownStatus]
    );

    useEffect(() => {
        if (vehicle?.PublicationCurrentExtension > 0) {
            fetchServerTime(); //UPDATE SERVER TIME THEN CHECK COUNTDOWN STATUS WITH NEW SERVER TIME / END DATE
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle?.PublicationCurrentExtension, fetchServerTime]);

    useEffect(() => {
        if (serverTime) {
            updateCountDownStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverTime]);

    //Renderizar de nuevo el carousel
    useEffect(() => {
        window.bronson
            .$$(`#card-vehicle-${vehicle.Id} .js-carousel`)
            .component('carousel');

        setTimeout(() => window.bronson.ComponentRegister.initAll(), 200);
    }, [vehicle.Id]);

    const tagMaxBidStatus = useCallback(() => {
        if (vehicle.maxBidAWStatus && vehicle.Status === 2) {
            return (
                <Tag
                    classes={[
                        'c-vehicle-card__tag-bid',
                        vehicle.maxBidAWStatus === 1 ? 'success' : 'error',
                    ]}
                    text={t.getTraduction(
                        vehicle.maxBidAWStatus === 1
                            ? 'WinningBid'
                            : 'BidExceeded'
                    )}
                    type="corner"
                />
            );
        }
    }, [t, vehicle.Status, vehicle.maxBidAWStatus]);

    const onShowDetail = useCallback(() => {
        dispatch({ type: 'SHOW_DETAIL' });
    }, []);

    const handleClickFavourite = useCallback(async () => {
        let status = false;
        if (vehicle.Status === 2 && state.countDownStatus < 2) {
            if (vehicle.IsFavorite) {
                await new VehicleService().deleteFavourite(
                    cookies.get(Globals.COOKIES.JWT).AccessToken,
                    vehicle.Id
                );
            } else {
                await new VehicleService().setFavourite(
                    cookies.get(Globals.COOKIES.JWT).AccessToken,
                    vehicle.Id
                );
                status = true;
            }

            updateVehiclesList({ ...vehicle, IsFavorite: status });
        }
    }, [cookies, state.countDownStatus, updateVehiclesList, vehicle]);

    const printCountDown = useCallback(() => {
        if (vehicle.Status > 2 || state.countDownStatus === null) return;
        const { label, classes } = getCountDownStyles(
            state.countDownStatus,
            vehicle,
            t
        );
        const text = (
            <CountDown
                dateTime={
                    state.countDownStatus === 0
                        ? vehicle?.PublicationDate
                        : vehicle?.PublicationCurrentExtension
                        ? vehicle?.PublicationEndDateVehicle
                        : vehicle?.PublicationEndDate
                }
                isFinishedParent={updateCountDownStatus}
                options={{
                    prevLabel: label,
                }}
                serverTime={serverTime}
            />
        );
        return <Tag classes={classes} text={text} type="corner" />;
    }, [updateCountDownStatus, serverTime, state.countDownStatus, t, vehicle]);

    const printFavouriteIcon = useCallback(
        () => (
            <VehicleFavouriteIcon
                IsFavorite={vehicle.IsFavorite}
                handleClickFavourite={handleClickFavourite}
            />
        ),
        [handleClickFavourite, vehicle.IsFavorite]
    );

    const onDisableButtons = useCallback(
        payload =>
            dispatch({
                type: 'CHANGE_WEBSALES_STATUS',
                payload,
            }),
        []
    );

    const onUpdateLoadingStatus = useCallback(
        payload =>
            dispatch({
                type: 'SET_LOADING',
                payload,
            }),
        []
    );

    const printFooter = useCallback(
        type => (
            <Footer
                vehicle={vehicle}
                disabledVehicleWebSalesButtons={
                    state.disabledVehicleWebSalesButtons
                }
                countDownStatus={state.countDownStatus}
                setLoading={onUpdateLoadingStatus}
                loading={state.loading}
                disableButtons={onDisableButtons}
                updateVehiclesList={updateVehiclesList}
                type={type}
            />
        ),
        [
            onDisableButtons,
            onUpdateLoadingStatus,
            state.countDownStatus,
            state.disabledVehicleWebSalesButtons,
            state.loading,
            updateVehiclesList,
            vehicle,
        ]
    );

    const checkInactiveCard = useCallback(() => {
        if (
            vehicle.Status !== 2 ||
            vehicle.IsBlocked ||
            (state.countDownStatus >= 2 &&
                !vehicle.RepurchaseObligation &&
                !state.disabledVehicleWebSalesButtons)
        )
            return ' c-card--inactive';
        return '';
    }, [
        vehicle.Status,
        vehicle.IsBlocked,
        vehicle.RepurchaseObligation,
        state.countDownStatus,
        state.disabledVehicleWebSalesButtons,
    ]);

    const printDetail = useCallback(() => {
        if (!state.showDetail) return;
        return (
            <div className="c-card c-card--image c-vehicle-card u-text-left u-pb">
                <ModalDetailVehicle
                    vehicle={vehicle}
                    onShowDetail={onShowDetail}
                    printCountDown={printCountDown}
                    printFavouriteIcon={printFavouriteIcon}
                    printWebSalesOptions={() => printFooter('detailvehicle')}
                    updateVehiclesList={updateVehiclesList}
                    tagMaxBidStatus={tagMaxBidStatus}
                    fetchServerTime={fetchServerTime}
                    inactive={checkInactiveCard()}
                />
            </div>
        );
    }, [
        checkInactiveCard,
        fetchServerTime,
        onShowDetail,
        printCountDown,
        printFavouriteIcon,
        printFooter,
        state.showDetail,
        tagMaxBidStatus,
        updateVehiclesList,
        vehicle,
    ]);

    const renderInactiveLabel = useCallback(() => {
        let label;
        if (vehicle.Status > 2) {
            label = t.getTraduction('Sold');
        } else if (vehicle.IsBlocked) {
            label = t.getTraduction('Reserved');
        }
        return label && <p className="c-vehicle-card__watermark">{label}</p>;
    }, [t, vehicle.IsBlocked, vehicle.Status]);

    return (
        <>
            <article
                id={'card-vehicle-' + vehicle.Id}
                data-testid={'card-vehicle-' + vehicle.Id}
                className={
                    'c-card c-card--image c-vehicle-card u-text-left u-pb' +
                    checkInactiveCard()
                }
            >
                <div className="c-vehicle-card__carousel-container">
                    <Carousel
                        options={optionsCarousel}
                        onClickFunction={onShowDetail}
                    />
                    {renderInactiveLabel()}
                    {vehicle?.WebSalesId && (
                        <>
                            {printFavouriteIcon()}
                            {printCountDown()}
                            {tagMaxBidStatus()}
                        </>
                    )}
                </div>
                <Header
                    vehicle={vehicle}
                    onShowDetail={onShowDetail}
                    loading={state.loading}
                    countDownStatus={state.countDownStatus}
                    hideTrolley={vehicle?.HasAgExporter}
                />
                <Body vehicle={vehicle} translations={t} />
                {printFooter('vehiclecard')}
            </article>
            {printDetail()}
        </>
    );
};

VehicleAuctionCard.propTypes = {
    vehicle: vehicleProptypes,
};

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators({ ...vehicleActions }, dispatch),
});

export default memo(
    withCookies(
        withRouter(connect(null, mapDispatchToProps)(VehicleAuctionCard))
    )
);
